import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { CheckinCaptureResult } from '@goaltree/backend/convex/lib/voiceSession'
import type { ConvexReactClient } from 'convex/react'

import { localDateKey } from '@/hooks/use-coach-chat'
import {
  type FrameEffect,
  currentFrameAskGeneration,
  getSelfCamera,
  grabSelfFrame,
  patchFrameAsk,
  requestFrameAsk,
  setSelfCamera,
} from '@/lib/self-camera'

const IMAGE_MAX = 700_000
const UNSEEN =
  'The frame was taken. No description came back. Tell them you have the photo. Do not say you cannot see them.'
const SEE = 'This note is the frame you can see. Do not say you cannot see them.'

function blobToBase64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const data = String(reader.result ?? '')
      resolve(data.slice(data.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

function base64ToBlob(b64: string) {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: 'image/jpeg' })
}

async function upload(convex: ConvexReactClient, blob: Blob) {
  const uploadUrl = await convex.mutation(api.habitPhotos.uploadUrl, {})
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: { 'Content-Type': blob.type || 'image/jpeg' },
    body: blob,
  })
  if (!res.ok) throw new Error('upload')
  return ((await res.json()) as { storageId: Id<'_storage'> }).storageId
}

// Grab one frame, ask, and save it as today's photo check-in when they say yes.
export async function captureCheckinPhoto(
  convex: ConvexReactClient,
  habitId: string,
): Promise<CheckinCaptureResult> {
  if (!habitId) return { ok: false, error: 'Pass the habit id.' }
  const brief = await convex
    .query(api.voiceCall.nodeBrief, { nodeId: habitId as Id<'nodes'> })
    .catch(() => null)
  if (!brief?.habit)
    return { ok: false, error: 'No habit with that id. Use an id from their habit list.' }
  const habitTitle = brief.title
  const wasOn = getSelfCamera().on
  const on = await setSelfCamera(true)
  if (!on) {
    const denied = getSelfCamera().error === 'denied'
    return {
      ok: false,
      error: denied
        ? 'They blocked the camera. Ask them to allow it, turn on My camera, and try once more. Do not say you cannot see them.'
        : 'Their camera is unavailable. Ask them to turn on My camera and try once more. Do not say you cannot see them.',
    }
  }
  if (!wasOn)
    return {
      ok: false,
      error:
        'My camera just turned on. Ask them to point it at the habit and hold still, then call capture_checkin_photo once more. Do not say you cannot see them.',
    }
  const blob = await grabSelfFrame()
  if (!blob)
    return {
      ok: false,
      error:
        'My camera is on but this frame came out blank. Ask them to hold still and call capture_checkin_photo once more. Do not say you cannot see them.',
    }
  const url = URL.createObjectURL(blob)
  let currentBlob = blob
  let styledUrl: string | null = null
  let appliedLabel: string | undefined
  const keptLabel = () => appliedLabel
  let styleToken = 0
  let stylePromise: Promise<void> = Promise.resolve()
  const originalB64 = await blobToBase64(blob).catch(() => '')
  const stillOpen = () => currentFrameAskGeneration() !== null
  const decision = requestFrameAsk(brief.title, url, {
    apply: (effect) => {
      stylePromise = style(effect)
    },
    revert: () => {
      styleToken += 1
      if (styledUrl) URL.revokeObjectURL(styledUrl)
      styledUrl = null
      currentBlob = blob
      appliedLabel = undefined
      patchFrameAsk({
        url,
        appliedId: null,
        styling: false,
        styleError: false,
      })
    },
  })
  async function style(effect: FrameEffect) {
    if (!originalB64 || originalB64.length > IMAGE_MAX) {
      patchFrameAsk({ styling: false, styleError: true })
      return
    }
    const token = ++styleToken
    patchFrameAsk({ styling: true, styleError: false })
    try {
      const out = await convex.action(api.habitPhotosNode.styleCheckin, {
        habitTitle,
        imageBase64: originalB64,
        prompt: effect.prompt,
      })
      if (token !== styleToken) return
      const styled = base64ToBlob(out.imageBase64)
      const next = URL.createObjectURL(styled)
      if (styledUrl) URL.revokeObjectURL(styledUrl)
      styledUrl = next
      currentBlob = styled
      appliedLabel = effect.label
      patchFrameAsk({ url: next, appliedId: effect.id, styling: false, styleError: false })
    } catch {
      if (token !== styleToken) return
      patchFrameAsk({ styling: false, styleError: true })
    }
  }
  let seen: { note: string; looksDone: 'yes' | 'no' | 'unclear'; effects: FrameEffect[] } = {
    note: '',
    looksDone: 'unclear',
    effects: [],
  }
  let showedEffects = false
  try {
    if (originalB64 && originalB64.length <= IMAGE_MAX)
      seen = await convex.action(api.habitPhotosNode.describeCheckin, {
        habitTitle,
        imageBase64: originalB64,
      })
  } catch {
    // The ask still stands. The coach just has nothing to describe.
  }
  const effects = seen.effects ?? []
  if (stillOpen() && effects.length) {
    showedEffects = true
    patchFrameAsk({ effects })
  }
  const yes = await decision
  // Let an in-progress restyle finish so the saved photo keeps the chosen look.
  await stylePromise
  URL.revokeObjectURL(url)
  if (styledUrl) URL.revokeObjectURL(styledUrl)
  const habit = habitTitle.split('///')[0].trim()
  const kept = keptLabel()
  const seenFields = {
    habit,
    note: seen.note.trim() || UNSEEN,
    looksDone: seen.looksDone,
    hint: SEE,
    ...(showedEffects ? { effects: effects.map((effect) => effect.label) } : {}),
    ...(yes && kept ? { effect: kept } : {}),
  }
  if (!yes)
    return { ok: true, saved: false, ...seenFields, reason: 'They said not to save this photo.' }
  try {
    const storageId = await upload(convex, currentBlob)
    const date = localDateKey()
    await convex.action(api.habitPhotosNode.save, {
      habitId: habitId as Id<'nodes'>,
      date,
      storageId,
    })
    if (!brief.photo)
      await convex
        .mutation(api.habitDots.setDotStyle, {
          habitId: habitId as Id<'nodes'>,
          style: { kind: 'photo' },
        })
        .catch(() => {})
    let checkedIn = !!brief.doneToday
    let notTicked = false
    if (!brief.doneToday) {
      try {
        await convex.mutation(api.nodes.complete, { taskId: habitId as Id<'nodes'> })
        checkedIn = true
      } catch {
        // A fasting habit, or a tick that raced. The photo is still saved.
        notTicked = true
      }
    }
    return {
      ok: true,
      saved: true,
      checkedIn,
      alreadyDone: !!brief.doneToday,
      ...seenFields,
      ...(notTicked ? { reason: 'The photo is saved. The habit was not ticked off.' } : {}),
    }
  } catch {
    return {
      ok: false,
      saved: false,
      ...seenFields,
      error: 'The photo did not save. Offer to try once more.',
    }
  }
}
